import { Drawer } from '@fountain/fountain-ui-components';
import { Button, IconButton, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Skeleton } from '@material-ui/lab';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { useApiService } from 'hooks/useApiService';
import globalMessages from 'shared/global/messages';

import { ConversationTranscript, fetchConversationTranscript } from '../api';
import { messages } from '../messages';
import { formatConversationDate } from './formatConversationDate';
import { RichStepCompact } from './RichStepCompact';
import { useTranscriptStyles } from './transcriptStyles';
import { TurnBadges } from './TurnBadges';

type TranscriptMessage =
  ConversationTranscript['turns'][number]['messages'][number];

const channels = {
  widget: messages.channelWidget,
  sms: messages.channelSms,
  whatsapp: messages.channelWhatsapp,
  email: globalMessages.email,
};

const TranscriptMessageView = ({
  message,
  openings,
}: {
  message: TranscriptMessage;
  openings: ConversationTranscript['openings'];
}) => {
  const intl = useIntl();
  const classes = useTranscriptStyles();
  const { part } = message;
  if (!part) return null;
  const applicant = message.role === 'user';
  const text =
    part.type === 'text'
      ? part.text
      : part.type === 'rich' && 'message' in part.step
        ? part.step.message
        : null;
  const time = DateTime.fromISO(message.created_at)
    .toLocal()
    .setLocale(intl.locale)
    .toFormat('h:mma')
    .toLowerCase();
  return (
    <div className={classNames(classes.message, { [classes.cai]: !applicant })}>
      <Typography component="div" className={classes.sender}>
        {intl.formatMessage(
          applicant ? messages.transcriptApplicant : messages.transcriptCai,
          { time },
        )}
      </Typography>
      {text && (
        <Typography component="div" className={classes.bubble}>
          {text}
        </Typography>
      )}
      {part.type === 'rich' && (
        <RichStepCompact step={part.step} variant="full" openings={openings} />
      )}
      {part.type === 'placeholder' && (
        <Typography className={classes.marker}>
          {intl.formatMessage(messages.transcriptStepPlaceholder, {
            stepName: part.step_name,
          })}
        </Typography>
      )}
    </div>
  );
};

export const TranscriptContent = ({
  transcript,
  renderFooter,
}: {
  transcript: ConversationTranscript;
  renderFooter?: (thread: ConversationTranscript['thread']) => ReactNode;
}) => {
  const classes = useTranscriptStyles();
  const intl = useIntl();
  const highlighted = useRef<HTMLDivElement>(null);
  const end = useRef<HTMLDivElement>(null);
  const { thread, applicant_link: applicantLink } = transcript;
  const anonymized = thread.is_anonymized;
  const visitor = thread.is_visitor;
  let name = thread.applicant_name;
  if (visitor) name = intl.formatMessage(messages.visitorApplicant);
  if (anonymized) name = intl.formatMessage(messages.anonymizedApplicant);
  const opening = visitor
    ? intl.formatMessage(messages.filterNoOpening)
    : (thread.opening?.title ?? intl.formatMessage(messages.unknownOpening));
  const channel = thread.channel && channels[thread.channel];
  const metadata = [
    opening,
    channel && intl.formatMessage(channel),
    thread.first_turn_at &&
      formatConversationDate(thread.first_turn_at, intl.locale),
  ]
    .filter(Boolean)
    .join(' · ');
  const link =
    applicantLink && !anonymized && !visitor
      ? `/${encodeURIComponent(applicantLink.account_slug)}/jobs/${encodeURIComponent(applicantLink.funnel_external_id)}/v2/stages/${encodeURIComponent(applicantLink.stage_external_id)}?applicantId=${encodeURIComponent(applicantLink.applicant_external_id)}&persistedPopupType=applicantDrawerPopup`
      : null;

  useEffect(() => {
    (highlighted.current ?? end.current)?.scrollIntoView?.({
      block: 'nearest',
    });
  }, [transcript]);

  return (
    <>
      <div className={classes.meta}>
        <div className={classes.nameRow}>
          <Typography
            variant={visitor || anonymized ? 'body2' : 'h4'}
            className={visitor || anonymized ? classes.mutedName : undefined}
          >
            {name}
          </Typography>
          {link && (
            <Button
              component={Link}
              role="link"
              to={link}
              className={classes.applicantLink}
            >
              {intl.formatMessage(messages.openApplicant)}
            </Button>
          )}
        </div>
        <Typography variant="body2" className={classes.metadata}>
          {metadata}
        </Typography>
      </div>
      <section
        className={classes.body}
        data-testid="transcript-messages"
        aria-labelledby="transcript-title"
        // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- HIR-860: keyboard users focus the transcript to scroll it
        tabIndex={0}
      >
        {transcript.truncated_before && (
          <Typography className={classes.marker}>
            {intl.formatMessage(messages.transcriptEarlier)}
          </Typography>
        )}
        {transcript.turns.map(turn => (
          <div
            key={turn.turn_id}
            ref={turn.highlighted ? highlighted : undefined}
            className={classNames(classes.turn, {
              [classes.highlighted]: turn.highlighted,
            })}
            aria-current={turn.highlighted ? 'true' : undefined}
            data-turn-id={turn.turn_id}
          >
            {!anonymized &&
              turn.messages.map(message => (
                <TranscriptMessageView
                  key={message.id}
                  message={message}
                  openings={transcript.openings}
                />
              ))}
            {!anonymized && turn.highlighted && (
              <div className={classes.feedback}>
                <TurnBadges
                  thumbsDown={turn.thumbs_down}
                  knowledgeGap={turn.knowledge_gap}
                />
              </div>
            )}
          </div>
        ))}
        {transcript.truncated_after && (
          <Typography className={classes.marker}>
            {intl.formatMessage(messages.transcriptLater)}
          </Typography>
        )}
        <div ref={end} />
      </section>
      {renderFooter && (
        <div className={classes.footer}>{renderFooter(thread)}</div>
      )}
    </>
  );
};

export const TranscriptPanel = ({
  accountSlug,
  agentId,
  threadUuid,
  turnId,
  opening,
  onClose,
  renderFooter,
}: {
  accountSlug: string;
  agentId: string;
  threadUuid: string;
  turnId?: string;
  opening?: string;
  onClose: () => void;
  renderFooter?: (thread: ConversationTranscript['thread']) => ReactNode;
}) => {
  const classes = useTranscriptStyles();
  const intl = useIntl();
  const close = useRef<HTMLButtonElement>(null);
  const service = useCallback(
    () =>
      fetchConversationTranscript(
        accountSlug,
        agentId,
        threadUuid,
        turnId,
        opening,
      ),
    [accountSlug, agentId, threadUuid, turnId, opening],
  );
  const { result, refetch } = useApiService(service);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', escape);
    return () => document.removeEventListener('keydown', escape);
  }, [onClose]);

  return (
    <Drawer
      open
      variant="persistent"
      handleClose={onClose}
      paperClassName={classes.paper}
      PaperProps={{ role: 'dialog', 'aria-labelledby': 'transcript-title' }}
      // Persistent drawers skip the initial transition unless appear is explicit.
      SlideProps={{
        appear: true,
        onEntered: () => close.current?.focus({ preventScroll: true }),
      }}
    >
      <div className={classes.header}>
        <Typography variant="h3" id="transcript-title">
          {intl.formatMessage(messages.transcriptTitle)}
        </Typography>
        <IconButton
          ref={close}
          className={classes.close}
          aria-label={intl.formatMessage(globalMessages.close)}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </div>
      {result.isLoading && (
        <div className={classes.body} aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} variant="rect" height={48} />
          ))}
        </div>
      )}
      {result.status === 'error' && (
        <div className={classes.body}>
          <div className={classes.error} role="alert">
            <ErrorOutlineIcon />
            <Typography variant="body2">
              {intl.formatMessage(messages.loadError)}
            </Typography>
            <Button onClick={() => void refetch()}>
              {intl.formatMessage(globalMessages.tryAgain)}
            </Button>
          </div>
        </div>
      )}
      {result.status === 'ready' &&
        ('kind' in result.data ? (
          <div className={classes.state}>
            <Typography variant="h3">
              {intl.formatMessage(messages.transcriptNoAccessTitle)}
            </Typography>
            <Typography variant="body2" className={classes.metadata}>
              {intl.formatMessage(messages.transcriptNoAccessBody)}
            </Typography>
          </div>
        ) : (
          <TranscriptContent
            transcript={result.data}
            renderFooter={renderFooter}
          />
        ))}
    </Drawer>
  );
};
